import { useMemo } from "react";
import {
  BarChart3,
  Calendar,
  Globe,
  LayoutDashboard,
  Megaphone,
  Search,
  Sparkles,
  Store,
} from "lucide-react";

export type BusinessTierId = "free" | "pro" | "scale" | "ultra";
export type RequestServiceId = "site" | "site_marketing" | "marketing";
export type RequestTemplateId = RequestServiceId | "free_signup";

const TIER_IDS: BusinessTierId[] = ["free", "pro", "scale", "ultra"];
const PAIN_IDS = [1, 2, 3, 4] as const;
const PILLAR_ICONS = [Store, Globe, Search] as const;
const BENEFIT_ICONS = [LayoutDashboard, Calendar, Sparkles, BarChart3] as const;
const ONBOARDING_IDS = [1, 2, 3, 4] as const;
const REQUEST_SERVICE_IDS: RequestServiceId[] = ["site", "site_marketing", "marketing"];
const REQUEST_ICONS = { site: Globe, site_marketing: Sparkles, marketing: Megaphone } as const;
const FAQ_IDS = [1, 2, 3, 4, 5] as const;
const PRIORITY_CITY_SLUGS = [
  { slug: "jesolo", key: "Jesolo" },
  { slug: "cavallino", key: "Cavallino" },
  { slug: "caorle", key: "Caorle" },
  { slug: "venezia", key: "Venezia" },
  { slug: "bibione", key: "Bibione" },
  { slug: "eraclea", key: "Eraclea" },
] as const;

export const SELF_SERVE_TIER_IDS = new Set<BusinessTierId>(["pro", "scale"]);

export function useForBusinessesContent(t: (key: string, fallback?: string) => string) {
  return useMemo(() => {
    const splitLines = (key: string) =>
      t(key, "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    const tiers = TIER_IDS.map((id) => {
      const minKey =
        id === "pro" ? "business.min.6" : id === "scale" ? "business.min.12" : id === "ultra" ? "business.period.quote" : null;
      return {
        id,
        name: t(`business.tier.${id}.name`),
        headline: t(`business.tier.${id}.headline`),
        price: id === "ultra" ? t("business.period.from") + " " + t(`business.tier.${id}.price`) : t(`business.tier.${id}.price`),
        period: t("business.period.month"),
        commission: t(`business.tier.${id}.commission`),
        minDuration: minKey ? t(minKey) : null,
        includes: splitLines(`business.tier.${id}.includes`),
        excludes: id === "free" ? splitLines(`business.tier.${id}.excludes`) : [],
        highlighted: id === "pro",
        cta: t(`business.tier.${id}.cta`),
        customQuote: id === "ultra",
      };
    });

    const requestServices = REQUEST_SERVICE_IDS.map((id) => ({
      id,
      name: t(`business.service.${id}.name`),
      headline: t(`business.service.${id}.headline`),
      icon: REQUEST_ICONS[id],
      bullets: splitLines(`business.service.${id}.bullets`),
      cta: t(`business.service.${id}.cta`),
    }));

    const requestServiceTemplates: Record<RequestTemplateId, string> = {
      free_signup: t("business.template.free_signup"),
      site: t("business.template.site"),
      site_marketing: t("business.template.site_marketing"),
      marketing: t("business.template.marketing"),
    };

    const portfolioProjects = [
      {
        id: "jservice-jesolo",
        client: t("business.portfolio.jservice.client"),
        city: t("business.portfolio.jservice.city"),
        sector: t("business.portfolio.jservice.sector"),
        url: "https://jservicejesolo.com/",
        description: t("business.portfolio.jservice.desc"),
        highlights: splitLines("business.portfolio.jservice.highlights"),
      },
    ];

    const priorityCities = PRIORITY_CITY_SLUGS.map((c) => ({
      slug: c.slug,
      name: c.key,
    }));

    const painPoints = PAIN_IDS.map((i) => t(`business.pain.${i}`));

    const valuePillars = PILLAR_ICONS.map((icon, idx) => {
      const i = idx + 1;
      return {
        title: t(`business.pillar.${i}.title`),
        description: t(`business.pillar.${i}.desc`),
        icon,
      };
    });

    const benefitBlocks = BENEFIT_ICONS.map((icon, idx) => {
      const i = idx + 1;
      return {
        title: t(`business.benefit.${i}.title`),
        description: t(`business.benefit.${i}.desc`),
        icon,
      };
    });

    const onboardingSteps = ONBOARDING_IDS.map((step) => ({
      step: String(step),
      title: t(`business.onboarding.${step}.title`),
      text: t(`business.onboarding.${step}.text`),
    }));

    const freeIncludes = splitLines("business.free.includes");
    const vitrineLines = splitLines("business.vitrine.lines");

    const heroStats = [
      { label: t("business.hero.stat1.label"), sub: t("business.hero.stat1.sub") },
      { label: t("business.hero.stat2.label"), sub: t("business.hero.stat2.sub") },
      { label: t("business.hero.stat3.label"), sub: t("business.hero.stat3.sub") },
    ];

    const showcaseRows = [
      { label: t("business.showcase.row1"), done: true },
      { label: t("business.showcase.row2"), done: true },
      { label: t("business.showcase.row3"), done: true },
      { label: t("business.showcase.row4"), done: false },
    ];

    const formChips: { id: RequestTemplateId; label: string }[] = [
      { id: "free_signup", label: t("business.form.chip.free") },
      { id: "site", label: t("business.form.chip.site") },
      { id: "site_marketing", label: t("business.form.chip.site_mkt") },
      { id: "marketing", label: t("business.form.chip.mkt") },
    ];

    const faqItems = FAQ_IDS.map((i) => ({
      q: t(`business.faq.${i}.q`),
      a: t(`business.faq.${i}.a`),
    }));

    const faqJsonLd = [1, 2, 3].map((i) => ({
      q: t(`business.jsonld.faq.${i}.q`),
      a: t(`business.jsonld.faq.${i}.a`),
    }));

    return {
      tiers,
      requestServices,
      requestServiceTemplates,
      portfolioProjects,
      priorityCities,
      painPoints,
      valuePillars,
      benefitBlocks,
      onboardingSteps,
      freeIncludes,
      vitrineLines,
      heroStats,
      showcaseRows,
      formChips,
      faqItems,
      faqJsonLd,
    };
  }, [t]);
}
